/**
 * Shared, dependency-free session primitives (ADR-0036).
 *
 * `AxiosService` needs the refresh loop (to recover a 401) and the refresh loop
 * needs the API base URL and the CSRF value. Putting those two constants here
 * instead of in `AxiosService` keeps the dependency a straight line rather than
 * the circular import §R forbids.
 */
import { runtimeEnv } from "../config/runtime-env";

export const API_BASE_URL = runtimeEnv("VITE_API_BASE_URL");

/** Header name for the double-submit CSRF echo. */
export const CSRF_HEADER = "X-CSRF-Token";

/** Name of the JS-readable CSRF cookie svc-02 sets alongside `penn_rt`. */
const CSRF_COOKIE_NAME = "penn_csrf";

/**
 * Last CSRF value seen in a response body. The cookie is the source of truth,
 * but reading it back is not guaranteed — a `Domain`-scoped cookie set by the
 * API host is readable by the SPA host, yet a stricter future setup, or a
 * browser blocking third-party cookie access, could hide it. Caching the body
 * copy means the header can still be echoed.
 */
let cachedCsrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  cachedCsrfToken = token;
}

/** The CSRF value to echo: the live cookie if readable, else the cached one. */
export function readCsrfCookie(): string | null {
  if (typeof document !== "undefined" && typeof document.cookie === "string") {
    for (const part of document.cookie.split(";")) {
      const [rawName, ...rest] = part.split("=");
      if (rawName?.trim() === CSRF_COOKIE_NAME) {
        const value = rest.join("=").trim();
        if (value.length > 0) return decodeURIComponent(value);
      }
    }
  }
  return cachedCsrfToken;
}
